import * as React from 'react';
import { CommType, Email, Event, openEmailArguments, Status } from '$types';
import * as Parts from './Parts';
import styled, { css } from 'styled-components';

interface Props {
    patientName: string;
    representative: {
        photo: string | null;
        name: string | null;
    };
    practiceName: string;
    email: Email;
    openModal: (args: openEmailArguments) => void;
    retry?: (comm: Event) => void;
    attachmentsEnabled?: boolean;
}

export const EmailEvent = (props: Props) => {
    const { email } = props;

    const snippet = email.identity
        ? makeSnippet(email.text_content)
        : email.text_content;

    return (
        <Parts.Container
            data-testid={email.id}
            title={email.email_data.subject}
            role="listitem"
            data-cy="comm"
            inbound={email.inbound}
        >
            {buildEvent(props, snippet)}
        </Parts.Container>
    );
};

/**
 * Build event
 * @param props
 * @param snippet
 */
const buildEvent = (props: Props, snippet: string) => {
    const { email, retry, openModal, attachmentsEnabled } = props;
    const color = email.inbound ? 'gray-100' : 'primary-500';
    // temporary until timeline v2 is ready - [sc-4334]
    const status: string =
        typeof email.status === 'object'
            ? (email.status as Status).name.toLowerCase()
            : email.status.toLowerCase();
    const openable = Boolean(email.identity) && status !== 'failed';

    const handleOpen = () => {
        if (!openable) {
            return;
        }

        if (email.identity) {
            openModal({
                fromEmail: email.email_data.from_email,
                toEmail: email.email_data.to_email,
                subject: email.email_data.subject,
                status: email.status,
                commName: email.identity,
                date: email.datetime,
            });
        }
    };

    const failed = status === 'failed';

    return (
        <>
            <Parts.Avatar
                isAutomation={email.comm_type === CommType.MarketingEmail}
                type="Email"
                color={color}
                inbound={email.inbound}
                {...props}
            />
            <Parts.Event inbound={email.inbound}>
                <Clickable
                    data-testid={`clickable-${email.id}`}
                    onClick={handleOpen}
                    clickable={openable}
                >
                    <Parts.Content
                        type={email.type}
                        title={email.email_data.subject}
                        color={color}
                        inbound={email.inbound}
                    >
                        {snippet}
                    </Parts.Content>
                </Clickable>
                <Parts.Metadata
                    failed={failed}
                    retrying={email.retry}
                    retry={() => retry && retry(email)}
                    inbound={email.inbound}
                >
                    <Parts.Status
                        status={status}
                        date={email.datetime}
                        inbound={email.inbound}
                    />
                </Parts.Metadata>
                {attachmentsEnabled && email.attachments && (
                    <Parts.Attachments attachments={email.attachments} />
                )}
            </Parts.Event>
            {(failed || email.retry?.active) && <Parts.FailedIcon />}
        </>
    );
};

const makeSnippet = (text: string): string => {
    let snippet = text.substring(0, 150);
    if (snippet.length !== text.length) {
        snippet = snippet + '...';
    }
    return snippet;
};

interface ContainerProps {
    clickable?: boolean;
}

export const Clickable = styled.div<ContainerProps>`
    ${({ clickable }) => {
        return (
            clickable &&
            css`
                &:hover {
                    cursor: pointer;
                }
            `
        );
    }}
`;
